import { useQuery, useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { WorkflowApi } from './api';
import type { ProcessCommandRequest, MediaPayload, LocationPayload, ChannelResponse, TaskContext } from './types';

const QUERY_KEYS = {
  all: ['workflows'] as const,
  list: () => [...QUERY_KEYS.all, 'list'] as const,
  instances: () => [...QUERY_KEYS.all, 'instances'] as const,
  currentTask: (botCode: string) => [...QUERY_KEYS.all, 'current-task', botCode] as const,
  workflowTasks: (botCode: string) => [...QUERY_KEYS.all, 'tasks', botCode] as const,
};

/**
 * Get active workflow instances (running workflows with pending tasks)
 * Returns the most recent instance per workflow template
 */
export const useWorkflowInstances = () => {
  return useQuery({
    queryKey: QUERY_KEYS.instances(),
    queryFn: () => WorkflowApi.getWorkflowInstances(),
  });
};

/**
 * Get current task for a specific workflow
 */
export const useCurrentTask = (botCode: string, sourceKey: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.currentTask(botCode),
    queryFn: () => WorkflowApi.getCurrentTask(botCode, sourceKey),
    enabled: !!sourceKey && !!botCode,
  });
};

/**
 * Get all tasks for a specific workflow
 */
export const useWorkflowTasks = (botCode: string, sourceKey: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.workflowTasks(botCode),
    queryFn: () => WorkflowApi.getWorkflowTasks(botCode, sourceKey),
    enabled: !!sourceKey && !!botCode,
  });
};

/**
 * Process any workflow command
 */
export const useProcessCommand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ProcessCommandRequest) => WorkflowApi.processCommand(request),
    onSuccess: (_, variables) => {
      // Invalidate current task query for the affected workflow
      if (variables.command.includes('/start')) {
        const botCode = variables.command.split(' ')[1];
        if (botCode) {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(botCode) });
        }
      }
      // Invalidate all workflow lists
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.list() });
    },
  });
};

/**
 * Start a workflow
 */
export const useStartWorkflow = (
  options?: Omit<
    UseMutationOptions<
      ChannelResponse,
      Error,
      { userId: string; orgId: string; botCode: string },
      unknown
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, orgId, botCode }: { userId: string; orgId: string; botCode: string }) =>
      WorkflowApi.startWorkflow(userId, orgId, botCode),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(variables.botCode) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.list() });
      // Call custom onSuccess callback if provided
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      // Call custom onError callback if provided
      options?.onError?.(error, variables, context);
    },
  });
};

/**
 * Submit text response (text, number, date, address task types)
 */
export const useSubmitText = (botCode: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      text,
      taskContext,
      sessionId,
    }: {
      userId: string;
      orgId: string;
      text: string;
      taskContext: TaskContext;
      sessionId?: string;
    }) => WorkflowApi.submitText(userId, orgId, text, taskContext, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(botCode) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workflowTasks(botCode) });
    },
  });
};

/**
 * Submit media response (photo, video, document, media task types)
 */
export const useSubmitMedia = (botCode: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      media,
      taskContext,
      sessionId,
    }: {
      userId: string;
      orgId: string;
      media: MediaPayload;
      taskContext: TaskContext;
      sessionId?: string;
    }) => WorkflowApi.submitMedia(userId, orgId, media, taskContext, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(botCode) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workflowTasks(botCode) });
    },
  });
};

/**
 * Submit location response (location, location_check task types)
 */
export const useSubmitLocation = (botCode: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      location,
      taskContext,
      sessionId,
    }: {
      userId: string;
      orgId: string;
      location: LocationPayload;
      taskContext: TaskContext;
      sessionId?: string;
    }) => WorkflowApi.submitLocation(userId, orgId, location, taskContext, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(botCode) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workflowTasks(botCode) });
    },
  });
};

/**
 * Submit choice selection
 */
export const useSubmitChoice = (botCode: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      choiceId,
      choiceText,
      taskContext,
      sessionId,
    }: {
      userId: string;
      orgId: string;
      choiceId: string;
      choiceText: string;
      taskContext: TaskContext;
      sessionId?: string;
    }) => WorkflowApi.submitChoice(userId, orgId, choiceId, choiceText, taskContext, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(botCode) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workflowTasks(botCode) });
    },
  });
};

/**
 * Skip current task
 */
export const useSkipTask = (botCode: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      taskContext,
      sessionId,
    }: {
      userId: string;
      orgId: string;
      taskContext: TaskContext;
      sessionId?: string;
    }) => WorkflowApi.skipTask(userId, orgId, taskContext, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(botCode) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workflowTasks(botCode) });
    },
  });
};

/**
 * Undo last action
 */
export const useUndo = (botCode: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      sessionId,
    }: {
      userId: string;
      orgId: string;
      sessionId?: string;
    }) => WorkflowApi.undo(userId, orgId, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(botCode) });
    },
  });
};

/**
 * Confirm WIP task completion (yes = mark complete, no = keep in progress)
 */
export const useConfirmWip = (botCode: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      confirm,
      taskContext,
      sessionId,
    }: {
      userId: string;
      orgId: string;
      confirm: 'yes' | 'no';
      taskContext: TaskContext;
      sessionId?: string;
    }) => WorkflowApi.confirmWip(userId, orgId, confirm, taskContext, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(botCode) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workflowTasks(botCode) });
    },
  });
};

/**
 * Exit workflow
 */
export const useExitWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      botCode,
      sessionId,
    }: {
      userId: string;
      orgId: string;
      botCode: string;
      sessionId?: string;
    }) => WorkflowApi.exitWorkflow(userId, orgId, botCode, sessionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentTask(variables.botCode) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.list() });
    },
  });
};
